import React, { useEffect, useState } from 'react';
import { Table, Input, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getReportApiByKey } from '@app/api/reports.api';
import { useLocation } from 'react-router-dom';

const { Title } = Typography;

interface TypeEntry {
  id: string;
  name: string;
  total: number;
}

interface Category {
  id: number;
  code: string;
  name: string;
  total: number;
  types: { [key: string]: TypeEntry };
}

interface Department {
  id: number;
  code: string;
  name: string;
  total: number;
  samples: any[];
  categories?: { [key: string]: Category };
}

interface RawData {
  report: {
    [departmentId: string]: Department;
  };
}

interface TableRow {
  key: string;
  name: string;
  total: number;
  level: number;
  children?: TableRow[];
}

interface PRLicenseReportTableProps {
  reportKey: string;
  filters: any;
}

const reportConfig = {
  'pr-earnings-by-departments': { totalTitle: 'Total Earnings (LKR)', isCurrency: true },
  'pr-count-by-departments': { totalTitle: 'Total Count', isCurrency: false },
};

// SearchFilter component
const SearchFilter: React.FC<{ onSearch: (keyword: string) => void }> = ({ onSearch }) => (
  <div className="hide-in-pdf" style={{ marginBottom: 16 }}>
    <Input.Search
      placeholder="Search departments, categories or types..."
      allowClear
      onChange={(e) => onSearch(e.target.value)}
      onSearch={onSearch}
      enterButton
      style={{ width: 300 }}
    />
  </div>
);

export const PRLicenseReportTable: React.FC<PRLicenseReportTableProps> = ({ reportKey, filters }) => {
  const [rawData, setRawData] = useState<RawData | null>(null);
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [filteredData, setFilteredData] = useState<TableRow[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  type ReportKey = keyof typeof reportConfig;
  const config = reportConfig[reportKey as ReportKey];

  // Add row background styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .department-row td { background-color: #ebedef !important; }
      .category-row td { background-color: #f8f9f9 !important; }
      .type-row td { background-color: #ffffff !important; }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!filters || !reportKey) return;

      const reportApi = getReportApiByKey(reportKey);
      if (!reportApi) {
        message.error('Invalid report key.');
        return;
      }

      try {
        const response: RawData = await reportApi.generateReport(filters);
        setRawData(response);
      } catch (err) {
        message.error('Failed to fetch report data.');
        console.error(err);
      }
    };

    fetchData();
  }, [filters, reportKey]);

  useEffect(() => {
    if (!rawData || !rawData.report) return;

    const rows: TableRow[] = [];

    // Iterate through departments in the report
    Object.entries(rawData.report).forEach(([departmentId, department]) => {
      // Skip if department doesn't have required properties
      if (!department.name || department.total === undefined) return;

      const departmentRow: TableRow = {
        key: `dept-${departmentId}`,
        name: `${department.name} (${department.code})`,
        total: department.total,
        level: 0,
        children: [],
      };

      // Process categories if they exist
      if (department.categories) {
        Object.entries(department.categories).forEach(([categoryId, category]) => {
          const categoryRow: TableRow = {
            key: `dept-${departmentId}-cat-${categoryId}`,
            name: `${category.name} (${category.code})`,
            total: category.total,
            level: 1,
            children: [],
          };

          // Process types if they exist
          if (category.types) {
            Object.entries(category.types).forEach(([typeId, type]) => {
              const typeRow: TableRow = {
                key: `dept-${departmentId}-cat-${categoryId}-type-${typeId}`,
                name: type.name,
                total: type.total,
                level: 2,
              };
              categoryRow.children?.push(typeRow);
            });
          }

          departmentRow.children?.push(categoryRow);
        });
      }

      rows.push(departmentRow);
    });

    setTableData(rows);
    setFilteredData(rows);
    setExpandedRowKeys(collectAllKeys(rows));
  }, [rawData]);

  // Collect all keys for default expansion
  const collectAllKeys = (rows: TableRow[]): string[] => {
    let keys: string[] = [];
    for (const row of rows) {
      keys.push(row.key);
      if (row.children) {
        keys = keys.concat(collectAllKeys(row.children));
      }
    }
    return keys;
  };

  const handleSearch = (keyword: string) => {
    if (!keyword || keyword.trim() === '') {
      setFilteredData(tableData);
      return;
    }

    const search = (nodes: TableRow[]): TableRow[] => {
      const results: TableRow[] = [];

      for (const department of nodes) {
        const departmentMatches = department.name.toLowerCase().includes(keyword.toLowerCase());
        const filteredCategories: TableRow[] = [];

        if (department.children) {
          for (const category of department.children) {
            const categoryMatches = category.name.toLowerCase().includes(keyword.toLowerCase());
            const filteredTypes: TableRow[] = [];

            if (category.children) {
              for (const type of category.children) {
                if (type.name.toLowerCase().includes(keyword.toLowerCase())) {
                  filteredTypes.push(type);
                }
              }
            }

            if (categoryMatches || filteredTypes.length > 0) {
              filteredCategories.push({
                ...category,
                children: filteredTypes.length > 0 ? filteredTypes : category.children,
              });
            }
          }
        }

        if (departmentMatches || filteredCategories.length > 0) {
          results.push({
            ...department,
            children: filteredCategories.length > 0 ? filteredCategories : department.children,
          });
        }
      }

      return results;
    };

    setFilteredData(search(tableData));
  };

  const columns: ColumnsType<TableRow> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (value, row) => {
        const level = row.level;
        return (
          <div
            style={{
              paddingLeft: `${level * 16}px`,
              fontWeight: level === 0 ? 'bold' : 'normal',
            }}
          >
            {value}
          </div>
        );
      },
    },
    {
      title: config.totalTitle,
      dataIndex: 'total',
      key: 'total',
      render: (value) => (config.isCurrency ? `LKR ${value.toLocaleString()}/=` : value.toLocaleString()),
    },
  ];

  return (
    <div>
      <Title level={4}>{config.totalTitle.split('(')[0]} by Departments</Title>
      <SearchFilter onSearch={handleSearch} />
      <Table
        columns={columns}
        dataSource={filteredData}
        pagination={false}
        rowKey="key"
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
        }}
        rowClassName={(record) => {
          if (record.level === 0) return 'department-row';
          if (record.level === 1) return 'category-row';
          if (record.level === 2) return 'type-row';
          return '';
        }}
      />
    </div>
  );
};
